import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { DonateContent } from '../../components/DonateContent';
import ResultSMap from '../../components/ResultSMap';
import { TOTAL_ROUNDS_MAX, resultPathColors } from '../../constants/game';
import { roundToTwoDecimal } from '../../util';
import {
    PageContainer,
    ContentWrapper,
    HeroSection,
    Title,
    Subtitle,
    ScoreHero,
    ScoreCircle,
    ScoreRing,
    ScoreRingBackground,
    ScoreRingProgress,
    ScoreValue,
    ScoreNumber,
    ScoreLabel,
    AccuracyBadge,
    AccuracyIcon,
    AccuracyText,
    SectionTitle,
    RoundsGrid,
    RoundCard,
    RoundIndicator,
    RoundInfo,
    RoundTitle,
    RoundLocation,
    RoundDistance,
    RoundScore,
    ActionsSection,
    PrimaryButton,
    MapSection,
    MapHeader,
    MapIcon,
    MapTitle,
    DonateWrapper,
    ConfettiPiece,
} from './ResultStyles';

export const SingleResult = () => {
    const lastResult = useSelector(state => state.result);
    const { totalScore, guessedPoints, mode, city, radius } = lastResult;

    const accuracy = useMemo(() => {
        if (!totalScore) return 0;
        return Math.round(totalScore / TOTAL_ROUNDS_MAX);
    }, [totalScore]);

    const getAccuracyEmoji = percent => {
        if (percent >= 80) return '🏆';
        if (percent >= 60) return '🎯';
        if (percent >= 40) return '👍';
        if (percent >= 20) return '🤔';
        return '😅';
    };

    const getAccuracyText = percent => {
        if (percent >= 80) return 'Vynikající!';
        if (percent >= 60) return 'Skvělé!';
        if (percent >= 40) return 'Dobré';
        if (percent >= 20) return 'Jde to';
        return 'Příště to bude lepší';
    };

    const showConfetti = accuracy >= 70;

    if (guessedPoints && guessedPoints.length) {
        return (
            <PageContainer>
                {/* Confetti for high scores */}
                {showConfetti && (
                    <>
                        {[...Array(20)].map((_, i) => (
                            <ConfettiPiece
                                key={i}
                                $delay={Math.random() * 2}
                                $x={Math.random() * 100}
                                $color={resultPathColors[i % resultPathColors.length]}
                            />
                        ))}
                    </>
                )}

                <ContentWrapper>
                    <HeroSection>
                        <Subtitle>Hra dokončena</Subtitle>
                        <Title>Tvoje výsledky</Title>
                    </HeroSection>

                    <ScoreHero>
                        <ScoreCircle>
                            <ScoreRing viewBox="0 0 100 100">
                                <ScoreRingBackground cx="50" cy="50" r="45" />
                                <ScoreRingProgress
                                    cx="50"
                                    cy="50"
                                    r="45"
                                    $percent={accuracy}
                                    $color={
                                        accuracy >= 70 ? '#22c55e' :
                                        accuracy >= 40 ? '#fbbf24' :
                                        '#c80707'
                                    }
                                />
                            </ScoreRing>
                            <ScoreValue>
                                <ScoreNumber>{Math.round(totalScore)}</ScoreNumber>
                                <ScoreLabel>bodů</ScoreLabel>
                            </ScoreValue>
                        </ScoreCircle>

                        <AccuracyBadge $percent={accuracy}>
                            <AccuracyIcon>{getAccuracyEmoji(accuracy)}</AccuracyIcon>
                            <AccuracyText>
                                {accuracy}% přesnost – {getAccuracyText(accuracy)}
                            </AccuracyText>
                        </AccuracyBadge>
                    </ScoreHero>

                    <SectionTitle>Přehled kol</SectionTitle>
                    <RoundsGrid>
                        {guessedPoints.map((round, i) => {
                            const color = resultPathColors[i] || '#6e45f9';
                            const cityInfo = round?.currentCity;
                            const locationText = cityInfo
                                ? `${cityInfo.obec}, ${cityInfo.okres}`
                                : null;

                            return (
                                <RoundCard key={i} $index={i} $color={color}>
                                    <RoundIndicator $color={color}>
                                        {i + 1}
                                    </RoundIndicator>
                                    <RoundInfo>
                                        <RoundTitle>Kolo {i + 1}</RoundTitle>
                                        {locationText && (
                                            <RoundLocation>{locationText}</RoundLocation>
                                        )}
                                    </RoundInfo>
                                    <RoundDistance>
                                        {roundToTwoDecimal(round.distance)} km
                                    </RoundDistance>
                                    <RoundScore $score={Math.round(round.score)}>
                                        {Math.round(round.score)} bodů
                                    </RoundScore>
                                </RoundCard>
                            );
                        })}
                    </RoundsGrid>

                    <ActionsSection>
                        <PrimaryButton to="/">
                            <span>🎮</span>
                            Hrát znovu
                        </PrimaryButton>
                    </ActionsSection>

                    <DonateWrapper>
                        <DonateContent marginBottom={0} withDonate />
                    </DonateWrapper>

                    <MapSection>
                        <MapHeader>
                            <MapIcon>🗺️</MapIcon>
                            <MapTitle>Mapa všech tipů</MapTitle>
                        </MapHeader>
                        <ResultSMap
                            guessedPoints={guessedPoints}
                            mode={mode}
                            city={city}
                            radius={radius}
                        />
                    </MapSection>
                </ContentWrapper>
            </PageContainer>
        );
    }

    return <Redirect to={{ pathname: '/' }} />;
};
